import { useEffect, useCallback, useRef } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  threshold?: number;
}

/**
 * Smooth infinite scroll with scroll event detection
 * Simpler and more reliable than IntersectionObserver for feed
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 500
}: UseInfiniteScrollOptions) => {
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(hasMore);
  const onLoadMoreRef = useRef(onLoadMore);
  const lastTriggerRef = useRef(0);

  // Keep refs in sync
  useEffect(() => {
    hasMoreRef.current = hasMore;
    console.log('📊 hasMoreRef updated:', hasMore);
  }, [hasMore]);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    console.log('📊 isLoadingRef updated:', isLoading);
  }, [isLoading]);

  // Scroll event handler with throttling
  const handleScroll = useCallback(() => {
    // Prevent duplicate calls within 500ms
    const now = Date.now();
    if (now - lastTriggerRef.current < 500) {
      return;
    }

    if (isLoadingRef.current) {
      console.log('⏸️ Infinite scroll - already loading');
      return;
    }

    if (!hasMoreRef.current) {
      console.log('⏸️ Infinite scroll - no more data');
      return;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const distanceFromBottom = documentHeight - (scrollTop + windowHeight);

    console.log('📏 Scroll position:', {
      scrollTop,
      windowHeight,
      documentHeight,
      distanceFromBottom,
      threshold,
      shouldTrigger: distanceFromBottom <= threshold
    });

    if (distanceFromBottom <= threshold) {
      console.log('🔽 Infinite scroll TRIGGERED!', {
        distanceFromBottom,
        threshold,
        hasMore: hasMoreRef.current,
        isLoading: isLoadingRef.current
      });

      lastTriggerRef.current = now;
      isLoadingRef.current = true;
      onLoadMoreRef.current();
    }
  }, [threshold]);

  // Attach scroll listener
  useEffect(() => {
    console.log('🎯 Attaching scroll listener');
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    setTimeout(() => {
      console.log('🔍 Initial scroll check');
      handleScroll();
    }, 500);

    return () => {
      console.log('🗑️ Removing scroll listener');
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // Auto-load if content is too short
  useEffect(() => {
    if (hasMore && !isLoading) {
      const checkContentHeight = () => {
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        
        if (documentHeight <= windowHeight + 100) {
          console.log('📏 Content too short, auto-loading more', {
            documentHeight,
            windowHeight
          });
          onLoadMore();
        }
      };

      const timer = setTimeout(checkContentHeight, 500);
      return () => clearTimeout(timer);
    }
  }, [hasMore, isLoading, onLoadMore]);

  // Reset loading flag when isLoading changes from true to false
  useEffect(() => {
    if (!isLoading && isLoadingRef.current) {
      console.log('✅ Load complete, resetting loading flag');
      isLoadingRef.current = false;
    }
  }, [isLoading]);
};

